import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Modal, IconButton, CircularProgress } from '@mui/material';
import { X } from 'lucide-react';
import { ApiCall } from '../../../helper/axios';
import { useSelector } from 'react-redux';
import { Dropdown } from '../../../common_components/FormInputs';
import AppButton from '../../../common_components/AppButton';
import useToast from '../../../common_components/Toast/useToast';
export default function AssignToListModal({ open, onClose, trackerId, selectedContactIds, onSuccess, apiBase = '/api/affiliate-tracker', }) {
    const token = useSelector((state) => state.auth.token);
    const toast = useToast();
    const [lists, setLists] = useState([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const [selectedListId, setSelectedListId] = useState('');
    const [assigning, setAssigning] = useState(false);
    const [creatingList, setCreatingList] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        setSelectedListId('');
        fetchLists();
    }, [open]);
    const fetchLists = async () => {
        setLoadingLists(true);
        try {
            const res = await ApiCall('GET', `${apiBase}/hubspot-lists`, undefined, { Authorization: token, 'x-module-key': 'affiliate_account_performance' });
            const data = res?.data;
            if (data?.success) {
                setLists(data.data || []);
            }
            else {
                toast.error('Failed to load HubSpot lists');
            }
        }
        catch {
            toast.error('Failed to load HubSpot lists');
        }
        finally {
            setLoadingLists(false);
        }
    };
    const handleCreateList = async (name) => {
        setCreatingList(true);
        try {
            const res = await ApiCall('POST', `${apiBase}/hubspot-create-list`, { name }, { Authorization: token, 'x-module-key': 'affiliate_account_performance' });
            const data = res?.data ?? res?.response?.data;
            if (data?.success && data?.data) {
                const listId = String(data.data.listId || data.data.id || '');
                const listName = data.data.name || name;
                if (listId && listId !== 'undefined') {
                    const newList = { listId, name: listName };
                    setLists(prev => [...prev, newList]);
                    setSelectedListId(newList.listId);
                    toast.success(`List "${newList.name}" created successfully!`);
                }
                else {
                    toast.error('Failed to resolve created list ID');
                }
            }
            else {
                toast.error(data?.message || 'Failed to create list');
            }
        }
        catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to create HubSpot list';
            toast.error(msg);
        }
        finally {
            setCreatingList(false);
        }
    };
    const cleanErrorMessage = (err) => {
        const msg = err?.response?.data?.message || err?.message || (typeof err === 'string' ? err : 'Failed to assign contacts');
        if (typeof msg !== 'string')
            return String(msg);
        const bodyMatch = msg.match(/Body:\s*(\{.*\})/s);
        if (bodyMatch) {
            try {
                const parsed = JSON.parse(bodyMatch[1]);
                if (Array.isArray(parsed.errors) && parsed.errors[0]?.message)
                    return parsed.errors[0].message;
                if (parsed.message) {
                    const inner = parsed.message.match(/"message"\s*:\s*"([^"]+)"/);
                    if (inner && inner[1])
                        return inner[1];
                    return parsed.message;
                }
            }
            catch { }
        }
        const simple = msg.match(/Message:\s*([^\n]+)/);
        if (simple && simple[1] && !simple[1].includes('An error occurred'))
            return simple[1].trim();
        if (msg.length > 120) {
            const firstLine = msg.split('\n')[0];
            if (firstLine && firstLine.length < 120)
                return firstLine;
        }
        return msg;
    };
    const handleAssign = async () => {
        if (!selectedContactIds || selectedContactIds.length === 0) {
            toast.error('No eligible contacts found. Only Approved contacts that have been synced to HubSpot can be assigned to a list.');
            return;
        }
        if (!selectedListId) {
            toast.error('Please select a contact list');
            return;
        }
        setAssigning(true);
        try {
            const res = await ApiCall('POST', `${apiBase}/hubspot-sync`, {
                type: 'assign_to_list',
                contact_ids: selectedContactIds,
                list_id: selectedListId,
            }, { Authorization: token, 'x-module-key': 'affiliate_account_performance' });
            const data = res?.data ?? res?.response?.data;
            if (data?.success) {
                toast.success('Contacts assigned to HubSpot list successfully!');
                onSuccess();
                onClose();
            }
            else {
                toast.error(cleanErrorMessage(data?.message || 'Failed to assign contacts'));
            }
        }
        catch (err) {
            toast.error(cleanErrorMessage(err));
        }
        finally {
            setAssigning(false);
        }
    };
    const listOptions = lists.map(l => ({ value: l.listId, label: l.name }));
    const hasEligibleContacts = Boolean(selectedContactIds && selectedContactIds.length > 0);
    return (_jsx(Modal, { open: open, onClose: onClose, children: _jsxs(Box, { sx: {
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: '#fff', borderRadius: '8px',
                boxShadow: '0px 20px 60px rgba(16,24,40,0.18)',
                width: 480, outline: 'none', p: 3,
            }, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }, children: [_jsx(Typography, { sx: { fontSize: '16px', fontWeight: 700, color: '#101828' }, children: "Contacts Assign to List" }), _jsx(IconButton, { size: "small", onClick: onClose, sx: { color: '#667085' }, children: _jsx(X, { size: 18 }) })] }), !hasEligibleContacts && (_jsx(Box, { sx: { bgcolor: '#FFFAEB', border: '1px solid #FEDF89', borderRadius: '8px', p: 1.5, mb: 2.5 }, children: _jsxs(Typography, { sx: { fontSize: '13px', color: '#B54708', fontWeight: 500, lineHeight: 1.4 }, children: ["\u26A0\uFE0F No eligible contacts available. Only ", _jsx("strong", { children: "Approved" }), " contacts that have already been synced to HubSpot (with a sync date) can be assigned to a list. Approve the contacts, then run ", _jsx("strong", { children: "Contact sync" }), "."] }) })), _jsxs(Box, { sx: { mb: 1 }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054', mb: 0.75 }, children: "Select Existing List" }), loadingLists ? (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, py: 1 }, children: [_jsx(CircularProgress, { size: 16, sx: { color: '#EE2A7B' } }), _jsx(Typography, { sx: { fontSize: '13px', color: '#667085' }, children: "Loading lists..." })] })) : (_jsx(Dropdown, { value: selectedListId, onChange: (v) => setSelectedListId(v), options: listOptions, displayValue: _jsx("span", { style: { fontSize: '13px', color: selectedListId ? '#101828' : '#98A2B3' }, children: creatingList
                                    ? 'Creating list...'
                                    : selectedListId
                                        ? lists.find(l => l.listId === selectedListId)?.name || 'Select Contact List'
                                        : 'Select Contact List' }), fullWidth: true, size: "md", searchable: true, disabled: creatingList, onCreate: handleCreateList }))] }), _jsx(Typography, { sx: { fontSize: '12px', color: '#98A2B3', mb: 3 }, children: "Static contact lists are synced from HubSpot." }), _jsx(AppButton, { onClick: handleAssign, disabled: assigning || loadingLists || creatingList || !selectedListId || !hasEligibleContacts, fullWidth: true, sx: {
                        bgcolor: '#EE2A7B', color: '#fff', fontWeight: 600,
                        fontSize: '14px', height: '44px', borderRadius: '8px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#D91F6A' },
                        '&.Mui-disabled': { bgcolor: '#F9FAFB', color: '#98A2B3' }
                    }, children: assigning ? _jsx(CircularProgress, { size: 18, sx: { color: '#fff' } }) : 'Assign Contact' })] }) }));
}
